import React, {RefObject, useCallback} from 'react';
import {getLinkPath} from 'app/utils';
import PokerTableFrame from 'app/components/pages/poker/PokerTableFrame';
import {
    clampTablePosition,
    getDefaultTablePosition,
    getWidgetSize,
    type TablePosition
} from 'app/components/pages/poker/proom/proomWidgetGeometry';
import type {ProomSessionContext} from 'app/components/pages/poker/proom/proomTableTypes';
import {useProomTableWindowDrag} from 'app/components/pages/poker/proom/useProomTableWindowDrag';
import {useProomTableWindowResize} from 'app/components/pages/poker/proom/useProomTableWindowResize';
import type {ProomTableInfo} from 'app/utils/proom/proomMessages';

export interface TableWindow {
    boardId: string
    externalId: string
    name: string
    blinds: string
    currency: string
    position: TablePosition | null
    width: number
    zIndex: number
    src: string
}

export interface ProomTableWindowItemProps {
    session: ProomSessionContext
    iframeRef: RefObject<HTMLIFrameElement>
    isMobile: boolean
    tableWindow: TableWindow
    tableSrc: string
    onBringToFront: (boardId: string) => void
    onClose: (boardId: string) => void
    onInfo: (boardId: string, info: ProomTableInfo) => void
    onLeave: (boardId: string) => void
    onTableTransfer: (boardId: string, newBoardId: string) => void
    onUserTokenRefreshed: (userToken: string) => void
    onPositionChange: (boardId: string, position: TablePosition) => void
    onSizeChange: (boardId: string, width: number) => void
}

const ProomTableWindowItem = ({
    session,
    iframeRef,
    isMobile,
    tableWindow,
    tableSrc,
    onBringToFront,
    onClose,
    onInfo,
    onLeave,
    onTableTransfer,
    onUserTokenRefreshed,
    onPositionChange,
    onSizeChange
}: ProomTableWindowItemProps): React.ReactElement => {
    const {boardId, name, blinds, currency, position, width, zIndex} = tableWindow;

    const tableBlindsLabel = [blinds, currency].filter(Boolean).join(' · ');
    const widgetSize = getWidgetSize(width);
    const desktopPosition = clampTablePosition(position || getDefaultTablePosition(0, width), width);

    const handleClose = useCallback(() => {
        onClose(boardId);
    }, [boardId, onClose]);

    const handleMaximize = useCallback(() => {
        onClose(boardId);
    }, [boardId, onClose]);

    const handleBringToFront = useCallback(() => {
        onBringToFront(boardId);
    }, [boardId, onBringToFront]);

    const handleInfo = useCallback((info: ProomTableInfo) => {
        onInfo(boardId, info);
    }, [boardId, onInfo]);

    const handleLeave = useCallback(() => {
        onLeave(boardId);
    }, [boardId, onLeave]);

    const handleTableTransfer = useCallback((newBoardId: string) => {
        onTableTransfer(boardId, newBoardId);
    }, [boardId, onTableTransfer]);

    const handleDragStart = useProomTableWindowDrag({
        boardId,
        desktopPosition,
        isMobile,
        onPositionChange,
        width
    });

    const handleResizePointerDown = useProomTableWindowResize({
        boardId,
        isMobile,
        onSizeChange,
        width
    });

    return <PokerTableFrame
        callbacks={{
            onBringToFront: handleBringToFront,
            onClose: handleClose,
            onDragStart: handleDragStart,
            onInfo: handleInfo,
            onLeave: handleLeave,
            onMaximize: handleMaximize,
            onResizePointerDown: handleResizePointerDown,
            onTableTransfer: handleTableTransfer,
            onUserTokenRefreshed
        }}
        iframeRef={iframeRef}
        isMobile={isMobile}
        layout={{
            height: widgetSize.height,
            position: desktopPosition,
            width: widgetSize.width,
            zIndex
        }}
        maximizeHref={getLinkPath(`/poker/table/${boardId}`)}
        mode="floating"
        session={session}
        table={{
            blinds: tableBlindsLabel,
            boardId,
            externalId: tableWindow.externalId,
            name,
            src: tableSrc
        }}
    />;
};

export default ProomTableWindowItem;
